import {downloadFile} from '@domain/file-download'
import {useEventsSearch, useEventsSearchDownload, useRequiredConfig} from '@domain/shifthandover'
import {useOnPageWithoutUnmountFocus} from '@hconnect/common/hooks'
import {useEndOfPageScrollDebounce} from '@hconnect/common/hooks/useListScrollDebounce'
import {EventsTab, ShiftEventsSortBy, SortOrder, ShiftNotFoundError} from '@hconnect/common/types'
import {getQuickSelectFilter} from '@hconnect/common/utils'
import type {PaginationOptions} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {Theme, useMediaQuery} from '@mui/material'
import {keepPreviousData} from '@tanstack/react-query'
import {ShiftEventDetailsWidget as SideCard} from '@widgets/shift-event-details'
import {getFilterDifference, Page, FilterOptions} from '@widgets/shift-event-filters'
import React, {useEffect, useState, ComponentType} from 'react'
import {useNavigate, useLocation} from 'react-router'

import {
  useTrackShiftEventsAnalytics,
  useShiftEventsPageFilters,
  useQueryParameterForViewMode,
  viewModeToUrlParams
} from '../hooks'
import {ViewMode} from '../types/viewMode.types'

import {EventsView, SideCardProps} from './EventsView'

const createUrl = (nextViewMode: ViewMode, currentPath: string): string => {
  const viewModeParams = viewModeToUrlParams(nextViewMode)
  return `${currentPath}?${viewModeParams}`
}

const MOBILE_ELEMENTS_PER_PAGE = 50

type EventsContainerProps = {
  plantId: string
  activeTab: EventsTab
  setActiveTab: (tab: EventsTab) => void
}

export const EventsContainer: React.FC<EventsContainerProps> = ({
  plantId,
  activeTab,
  setActiveTab
}) => {
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const config = useRequiredConfig(plantId)
  const {shifts, country} = config
  const timezone = useUpmPlantTimezone(plantId)
  const viewMode = useQueryParameterForViewMode(plantId)
  const getPlantNow = useUpmPlantNow(plantId)
  const trackShiftEventAnalytics = useTrackShiftEventsAnalytics({plantId, country})

  const [pageNumber, changePage] = useState<number>(0)
  const [itemsPerPage, setItemsPerPage] = useState<number>(50)
  const [filterOptions, setFilters] = useShiftEventsPageFilters(plantId, Page.Events, {
    sortBy: ShiftEventsSortBy.Created,
    sortOrder: SortOrder.Descending
  })
  // on the Events page there is a default time range that:
  // - does not apply for all users of useEventsSearch and
  // - should not end up as part of the url
  // there for a special filter option setting just for useEventsSearch is needed
  const extendedFilterOptions: FilterOptions = {
    ...filterOptions
  }
  if (!extendedFilterOptions.timeRange) {
    const [startDate, endDate] = getQuickSelectFilter('currentShift', shifts, getPlantNow())

    if (!startDate || !endDate) {
      throw new ShiftNotFoundError()
    }

    extendedFilterOptions.timeRange = {
      startDate: startDate,
      endDate: endDate
    }
  }

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const {data, isLoading, isFetching, refetch} = useEventsSearch(
    {
      plantId,
      timezone,
      shifts,
      getPlantNow,
      input: extendedFilterOptions,
      sortBy: extendedFilterOptions.sortBy || ShiftEventsSortBy.Created,
      sortOrder: extendedFilterOptions.sortOrder,
      pageNumber,
      itemsPerPage
    },
    {
      placeholderData: isMobile ? keepPreviousData : undefined,
      refetchOnWindowFocus: false
    }
  )
  useOnPageWithoutUnmountFocus(refetch)

  const eventDownloadQuery = useEventsSearchDownload({
    plantId,
    shifts,
    getPlantNow,
    input: extendedFilterOptions,
    sortBy: extendedFilterOptions.sortBy || ShiftEventsSortBy.Created,
    sortOrder: extendedFilterOptions.sortOrder,
    pageNumber,
    itemsPerPage
  })

  const shouldLoadNextPage = itemsPerPage < (data?.totalItemCount || 0)

  const {trackScrolling} = useEndOfPageScrollDebounce(() => {
    setItemsPerPage((items) => items + MOBILE_ELEMENTS_PER_PAGE)
  }, shouldLoadNextPage)

  useEffect(() => {
    if (isMobile) {
      document.addEventListener('scroll', trackScrolling)
    }

    return () => document.removeEventListener('scroll', trackScrolling)
  }, [isMobile, trackScrolling])

  useEffect(() => {
    if (isMobile) {
      setItemsPerPage(MOBILE_ELEMENTS_PER_PAGE)
      changePage(0)
    }
  }, [isMobile])

  const onPaginationChange = (pageNumber: number, itemsPerPage: number) => {
    changePage(pageNumber)
    setItemsPerPage(itemsPerPage)
  }

  const paginationOptions: PaginationOptions = {
    page: pageNumber,
    rowsPerPage: itemsPerPage,
    onPaginationChange: onPaginationChange,
    totalDataLength: data?.totalItemCount ?? 0
  }

  return (
    <EventsView
      plantId={plantId}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      activeFilter={filterOptions}
      events={data?.events}
      isLoading={isLoading || isFetching}
      viewMode={viewMode}
      initialCreateData={viewMode.mode === 'createMode' ? viewMode.initialData : undefined}
      setViewMode={(nextViewMode) => {
        void navigate(createUrl(nextViewMode, pathname))
      }}
      onFilterChange={(nextFilterOptions) => {
        changePage(0)
        if (isMobile) {
          setItemsPerPage(MOBILE_ELEMENTS_PER_PAGE)
        }
        setFilters(nextFilterOptions)
        trackShiftEventAnalytics({
          name: 'userAdjustsFilter',
          data: {
            filterPage: 'events',
            filterName: getFilterDifference(nextFilterOptions, filterOptions)
          }
        })
      }}
      isEventDownloadLoading={eventDownloadQuery.isRefetching}
      onEventsDownload={async () => {
        const response = await eventDownloadQuery.refetch()
        if (response.data) {
          downloadFile(response.data, `events_${plantId}.xlsx`)
          trackShiftEventAnalytics({name: 'userDownloadsEventsList'})
        }
      }}
      paginationOptions={isMobile ? undefined : paginationOptions}
      showBottomProgress={isMobile && shouldLoadNextPage}
      SideCard={SideCard as ComponentType<SideCardProps>}
      onCreationStart={(uuid) =>
        trackShiftEventAnalytics({name: 'onCreationStartEvent', data: {uuid}})
      }
      onCreationFinish={(uuid) =>
        trackShiftEventAnalytics({name: 'onCreationFinishEvent', data: {uuid}})
      }
      onSapPmNotification={() =>
        trackShiftEventAnalytics({name: 'onSapPmNotificationFeature', data: {timezone}})
      }
    />
  )
}
